import {
	Alert,
	Box,
	Breadcrumbs,
	Button,
	Grid,
	Paper,
	Stack,
	styled,
	TextField,
	Typography,
	useTheme,
} from '@mui/material'
import { alertModal, confirmAlert, hexToRgba, timerAlert } from 'util/util'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import ContentPasteSearchTwoToneIcon from '@mui/icons-material/ContentPasteSearchTwoTone'
import SaveAsTwoToneIcon from '@mui/icons-material/SaveAsTwoTone'
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone'
import { useEffect, useState } from 'react'
import {
	DataGridPremium,
	GridColDef,
	GridSelectionModel,
	useGridApiRef,
} from '@mui/x-data-grid-premium'
import { getSettings } from 'helpers/storage'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider'
import { PageBg } from 'components/ComponentStyles'
import { httpGet, httpPost } from 'api/api'
import { valuesInIterator } from 'util/objectUtil'

const PageBg2 = styled('div')(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	padding: '1rem',
	borderRadius: '0.5rem',
	background: `${hexToRgba(theme.palette.custom.wrapper, 1)}`,
	// filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
	[`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
		flexDirection: 'column',
		borderRadius: 0,
	},
	// backdropFilter: 'blur(10px)',
}))

const PageWrap = styled('div')(({ theme }) => ({
	marginBottom: '1rem',
	position: 'relative',
	top: '0',
	left: '0',
	right: '0',
	zIndex: '110',
	[`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
		//marginLeft: '-1.5rem',
		//marginRight: '-1.5rem',
		top: '0',
		//borderRadius: '0',
	},
}))

const BtnBox = styled(Box)(({ theme }) => ({
	// [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
	// 	marginTop: '1rem',
	// },
}))

const TitleText = styled(Typography)(({ theme }) => ({
	[`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
		display: 'none',
	},
}))

const locales = ['ko'] as const

// 조회 설정
type SearchType = {
	clientID: number | null
	clientName: string | null
	phoneNumber: string | null
	isActive: boolean | null
}

const initialSearch: SearchType = {
	clientID: null,
	clientName: null,
	phoneNumber: null,
	isActive: null,
}

// 그리드 데이터 설정
type GridDataType = {
	RowNum: number
	RowCrud: string
	ClientID: number
	ClientName: string
	PhoneNumber: string
	IsActive: boolean
}

const gridColumns: GridColDef[] = [
	{
		field: 'RowNum',
		headerName: 'No',
		width: 80,
		align: 'center',
		headerAlign: 'center',
	},
	{
		field: 'ClientID',
		headerName: '거래처코드',
		width: 120,
		align: 'center',
		headerAlign: 'center',
	},
	{
		field: 'ClientName',
		headerName: '거래처명',
		width: 200,
		align: 'left',
		headerAlign: 'center',
	},
	{
		field: 'PhoneNumber',
		headerName: '전화번호',
		width: 300,
		align: 'center',
		headerAlign: 'center',
		editable: true,
		renderCell: (params) => {
			return formatPhoneNumber(params.value)
		},
	},
	{
		field: 'IsActive',
		headerName: '사용여부',
		width: 120,
		align: 'center',
		headerAlign: 'center',
		renderCell: (params) => {
			return (
				<span
					style={{
						backgroundColor: params.value ? '#4caf50' : '#ccc',
						color: '#fff',
						padding: '2px 6px',
						borderRadius: '4px',
						fontSize: '0.8rem',
						display: 'inline-block',
						minWidth: '70px',
						textAlign: 'center',
					}}
				>
					{params.value ? '사용' : '미사용'}
				</span>
			)
		},
	},
]

const toDigitsOnlyPhone = (value: string) =>
	value.replace(/\D/g, '').slice(0, 11)

const formatPhoneNumber = (value: string) => {
	if (!value) return ''

	if (value.startsWith('02')) {
		if (value.length < 3) return value
		if (value.length < 6) return `${value.slice(0, 2)}-${value.slice(2)}`
		if (value.length < 10)
			return `${value.slice(0, 2)}-${value.slice(2, 5)}-${value.slice(5)}`
		return `${value.slice(0, 2)}-${value.slice(2, 6)}-${value.slice(6, 10)}`
	}

	if (value.length < 4) return value
	if (value.length < 7) return `${value.slice(0, 3)}-${value.slice(3)}`
	if (value.length < 11)
		return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`
	return `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7, 11)}`
}

const AutoSmsGlobalSetting = () => {
	const theme = useTheme()
	const isMdUp = theme.breakpoints.up('md')

	const gridRef = useGridApiRef()

	const env = getSettings()
	const [locale] = useState<(typeof locales)[number]>('ko')

	const [search, setSearch] = useState<SearchType>(initialSearch)

	const [disabledReset, setDisabledReset] = useState(false)
	const [disabledSearch, setDisabledSearch] = useState(false)
	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([])

	const [gridData, setGridData] = useState<GridDataType[]>([])
	const [gridLoading, setGridLoading] = useState(false)
	const [gridPageSize, setGridPageSize] = useState(env.row ? env.row : 100)

	const actionReset = () => {
		setSearch(initialSearch)
		actionSearch()
	}

	const actionSearch = () => {
		console.log('actionSearch', search)
		httpGet('autoSms/client/list', search).then((res) => {
			console.log(res)
			setGridData(res)
			gridRef.current.setPage(0) // 페이지 초기화
			setSelectionModel([]) // 선택 초기화
		})
	}

	const actionSave = () => {
		// 선택된 데이터 row 확인
		const selectedRows = gridRef.current.getSelectedRows()
		const valuesArray = valuesInIterator(selectedRows)
		if (valuesArray.length === 0) {
			alertModal('저장할 데이터를 선택해주세요.', 'warning', () => {})
			return
		}

		// valeusArray 안에 PhoneNumber 값이 null 이면 alertModal 띄우고 return
		const hasNullPhoneNumber = valuesArray.some((row) => !row.PhoneNumber)
		if (hasNullPhoneNumber) {
			alertModal(
				'전화번호가 없는 거래처가 있습니다. 전화번호를 확인해주세요.',
				'warning',
				() => {},
			)
			return
		}

		confirmAlert(
			'선택한 데이터를 저장하시겠습니까?',
			() => {
				setGridLoading(true)
				// valuesArray 를 순환하면서 RowCrud값을 C로 변경하는데 IsActive값이 true 이면 RowCrud값을 U로 변경
				valuesArray.forEach((row) => {
					if (row.IsActive) {
						row.RowCrud = 'U'
					} else {
						row.RowCrud = 'C'
					}
					row.IsActive = 1
				})
				const request = {
					json: [...valuesArray],
				}
				httpPost('autoSms/client/save', request)
					.then((res) => {
						console.log(res)
						timerAlert(res.result, 'success', () => {})
						actionSearch()
					})
					.finally(() => {
						setGridLoading(false)
					})
			},
			() => {},
		)
	}

	const actionDel = () => {
		// 선택된 데이터 row 확인
		const selectedRows = gridRef.current.getSelectedRows()
		const valuesArray = valuesInIterator(selectedRows)
		if (valuesArray.length === 0) {
			alertModal('삭제할 데이터를 선택해주세요.', 'warning', () => {})
			return
		}
		confirmAlert(
			'선택한 데이터를 삭제하시겠습니까?',
			() => {
				setGridLoading(true)
				valuesArray.forEach((row) => {
					row.RowCrud = 'D' // 삭제 시 RowCrud 값 설정
					row.IsActive = 0
				})
				const request = {
					json: [...valuesArray],
				}
				httpPost('autoSms/client/save', request)
					.then((res) => {
						console.log(res)
						timerAlert(res.result, 'success', () => {})
						actionSearch()
					})
					.finally(() => {
						setGridLoading(false)
					})
			},
			() => {},
		)
	}

	const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') actionSearch()
	}

	useEffect(() => {
		actionSearch()
	}, [])

	return (
		<>
			<Stack
				sx={{
					position: 'sticky',
					top: '50px',
					zIndex: 100,
					padding: '0.5rem',
					borderRadius: '0.5rem',
					backgroundColor:
						theme.palette.mode === 'dark' ? 'rgba(50, 48, 54, 1)' : '#fff',
					'@media (max-width: 600px)': {
						position: 'relative',
						paddingTop: '15px',
						top: '0px',
					},
				}}
			>
				<PageWrap style={{ marginBottom: '0.5rem' }}>
					<PageBg2 sx={{ padding: '0.3rem 0.3rem 0rem 0.5rem' }}>
						<Breadcrumbs
							separator={<NavigateNextIcon fontSize="small" />}
							aria-label="breadcrumb"
							sx={{
								'@media (max-width: 900px': {
									display: 'none',
								},
							}}
						>
							<TitleText
								key="3"
								color="text.primary"
								sx={{ fontWeight: '500', fontSize: 17 }}
							>
								검사결과 SMS 설정
							</TitleText>
						</Breadcrumbs>
						<BtnBox>
							<div>
								<span>
									<Button
										size="small"
										color="success"
										variant="contained"
										startIcon={isMdUp && <RotateLeftIcon />}
										sx={{
											marginRight: '0.5rem',
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
										disabled={disabledReset}
										onClick={actionReset}
									>
										초기화
									</Button>
								</span>
								<span>
									<Button
										size="small"
										color="primary"
										variant="contained"
										startIcon={isMdUp && <ContentPasteSearchTwoToneIcon />}
										sx={{
											marginRight: '0.5rem',
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
										disabled={disabledSearch}
										onClick={actionSearch}
									>
										조회
									</Button>
								</span>
								<span>
									<Button
										color="secondary"
										size="small"
										variant="contained"
										startIcon={isMdUp && <SaveAsTwoToneIcon />}
										sx={{
											marginRight: '0.5rem',
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
										onClick={actionSave}
										disabled={selectionModel.length === 0}
									>
										활성화
									</Button>
								</span>
								<span>
									<Button
										color="warning"
										size="small"
										variant="contained"
										startIcon={isMdUp && <DeleteForeverTwoToneIcon />}
										sx={{
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
										onClick={actionDel}
										disabled={selectionModel.length === 0}
									>
										비활성화
									</Button>
								</span>
							</div>
						</BtnBox>
					</PageBg2>
				</PageWrap>
				<Box
					onKeyUp={onKeyUp}
					sx={{
						borderTop:
							theme.palette.mode === 'dark'
								? '1px solid rgba(81, 81, 81, 1)'
								: '1px solid #ccc',
						paddingTop: '1rem',
					}}
				>
					<LocalizationProvider
						dateAdapter={AdapterDayjs}
						adapterLocale={locale}
					>
						<Grid
							container
							spacing={1}
							sx={{ marginTop: '0.1rem' }}
						>
							<Grid
								item
								xs={6}
								sm={3}
								lg={3}
							>
								<TextField
									label="거래처명"
									variant="outlined"
									size="small"
									type="text"
									fullWidth
									value={search.clientName || ''}
									onChange={(e) => {
										setSearch({
											...search,
											clientName: e.target.value,
										})
									}}
								/>
							</Grid>
							<Grid
								item
								xs={6}
								sm={3}
								lg={3}
							>
								<TextField
									label="거래처코드"
									variant="outlined"
									size="small"
									type="text"
									fullWidth
									value={search.clientID || ''}
									onChange={(e) => {
										setSearch({
											...search,
											clientID: e.target.value ? Number(e.target.value) : null,
										})
									}}
								/>
							</Grid>
							<Grid
								item
								xs={6}
								sm={3}
								lg={3}
							>
								<TextField
									label="전화번호"
									variant="outlined"
									size="small"
									type="text"
									fullWidth
									value={formatPhoneNumber(search.phoneNumber || '')}
									inputProps={{ inputMode: 'numeric', maxLength: 13 }}
									onChange={(e) => {
										const phoneNumber = toDigitsOnlyPhone(e.target.value)
										setSearch({
											...search,
											phoneNumber: phoneNumber || null,
										})
									}}
								/>
							</Grid>
						</Grid>
					</LocalizationProvider>
				</Box>
			</Stack>
			<Paper
				elevation={0}
				sx={{ width: '100%', overflow: 'hidden', mt: 2 }}
			>
				<Grid container>
					<Grid
						item
						xs={12}
					>
						<PageBg>
							<Box
								sx={{
									height: 'calc(100vh - 300px)',
									'@media (max-width: 1199px)': {
										height: '400px',
										overflow: 'auto',
									},
								}}
							>
								<DataGridPremium
									columns={gridColumns}
									rows={gridData}
									apiRef={gridRef}
									getRowId={(row) => row.RowNum}
									loading={gridLoading}
									pageSize={gridPageSize}
									onPageSizeChange={(newPageSize) => {
										setGridPageSize(newPageSize)
									}}
									rowsPerPageOptions={[50, 100, 200]}
									pagination
									checkboxSelection
									onSelectionModelChange={(newSelectionModel) => {
										setSelectionModel(newSelectionModel)
									}}
									selectionModel={selectionModel}
									initialState={{
										columns: {
											columnVisibilityModel: {
												RowNum: false,
											},
										},
									}}
								/>
							</Box>
						</PageBg>
					</Grid>
				</Grid>
			</Paper>
		</>
	)
}

export default AutoSmsGlobalSetting
